import { api } from '@/lib/http-client';
import { finDelDiaEnZona, inicioDelDiaEnZona } from '@/lib/timezone';
import type { QueryParams } from '@/types/api';
import type {
  AgruparCampanas,
  IntervaloReporte,
  OrdenProductos,
  ReportFiltros,
  ReportQueryExtras,
  ReporteCampanas,
  ReporteHorarios,
  ReporteMotivosNoCierre,
  ReporteProductos,
  ReporteResumen,
  ReporteSerie,
} from '@/types/reports';

/**
 * Módulo 10 — Reportes de solo lectura (`docs/REPORTES.md`, `backend/src/modules/reports`).
 *
 * Los seis endpoints son `GET /api/tenants/:tenantId/reports/...` y los ve cualquier miembro del
 * negocio (`requireTenant`, sin rol): es data del propio negocio, no una acción de administración.
 *
 * La parte delicada es la ventana de fechas. Los filtros de la pantalla son DÍAS de calendario
 * (`'2026-09-10'`, lo que se elige en un `<input type="date">`) y el backend espera INSTANTES ISO:
 * `desde` se manda como el arranque de ese día en la zona del negocio y `hasta` como su último
 * milisegundo (23:59:59.999), para que el día elegido entre completo. Mandar el día pelado haría que
 * el backend lo leyera como medianoche UTC, cinco horas antes del día real en America/Bogota, y el
 * reporte perdería la madrugada y contaría de más en el extremo opuesto.
 */

/** Nombre real de los parámetros en la query (los del backend, no se renombran). */
export const REPORT_PARAMS = {
  desde: 'desde',
  hasta: 'hasta',
  channel: 'channel',
  adId: 'adId',
  campaignId: 'campaignId',
  agrupar: 'agrupar',
  intervalo: 'intervalo',
  limite: 'limite',
  ordenar: 'ordenar',
} as const;

/**
 * Filtros → query params, sin mandar los que no van.
 *
 * El mismo objeto es el que entra en la query key de TanStack Query, así «filtro distinto» =
 * «entrada de caché distinta» (y como las fechas se convierten antes, dos zonas distintas tampoco
 * comparten caché por accidente).
 */
export function reportQueryFrom(
  filtros: Partial<ReportFiltros> = {},
  zonaHoraria: string,
  extras: ReportQueryExtras = {},
): QueryParams {
  const query: QueryParams = {};

  const desde = filtros.desde ? inicioDelDiaEnZona(filtros.desde, zonaHoraria) : null;
  if (desde) query[REPORT_PARAMS.desde] = desde.toISOString();

  const hasta = filtros.hasta ? finDelDiaEnZona(filtros.hasta, zonaHoraria) : null;
  if (hasta) query[REPORT_PARAMS.hasta] = hasta.toISOString();

  if (filtros.channel) query[REPORT_PARAMS.channel] = filtros.channel;
  if (filtros.adId) query[REPORT_PARAMS.adId] = filtros.adId.trim();
  if (filtros.campaignId) query[REPORT_PARAMS.campaignId] = filtros.campaignId.trim();

  if (extras.agrupar) query[REPORT_PARAMS.agrupar] = extras.agrupar;
  if (extras.intervalo) query[REPORT_PARAMS.intervalo] = extras.intervalo;
  if (extras.ordenar) query[REPORT_PARAMS.ordenar] = extras.ordenar;
  if (extras.limite) query[REPORT_PARAMS.limite] = extras.limite;

  return query;
}

/** Query params del reporte de publicidad, tipados en `agrupar`. */
export function reportCampanasQuery(
  filtros: Partial<ReportFiltros>,
  zonaHoraria: string,
  agrupar: AgruparCampanas,
): QueryParams {
  return reportQueryFrom(filtros, zonaHoraria, { agrupar });
}

/** Query params de la serie, tipados en `intervalo`. */
export function reportSerieQuery(
  filtros: Partial<ReportFiltros>,
  zonaHoraria: string,
  intervalo: IntervaloReporte,
): QueryParams {
  return reportQueryFrom(filtros, zonaHoraria, { intervalo });
}

/** Query params del ranking de productos, tipados en `limite` + `ordenar`. */
export function reportProductosQuery(
  filtros: Partial<ReportFiltros>,
  zonaHoraria: string,
  opciones: { limite: number; ordenar: OrdenProductos },
): QueryParams {
  return reportQueryFrom(filtros, zonaHoraria, opciones);
}

/** `GET .../reports/resumen` — KPIs del periodo (conversaciones, mensajes, órdenes, ventas, tasas). */
export function fetchReportResumen(tenantId: string, query: QueryParams): Promise<ReporteResumen> {
  return api.get<ReporteResumen>(`/api/tenants/${tenantId}/reports/resumen`, { query });
}

/** `GET .../reports/serie` — conversaciones, mensajes, órdenes, ventas e ingresos por día/semana/mes. */
export function fetchReportSerie(tenantId: string, query: QueryParams): Promise<ReporteSerie> {
  return api.get<ReporteSerie>(`/api/tenants/${tenantId}/reports/serie`, { query });
}

/** `GET .../reports/horarios` — mapa 7×24 de mensajes entrantes y conversaciones nuevas. */
export function fetchReportHorarios(tenantId: string, query: QueryParams): Promise<ReporteHorarios> {
  return api.get<ReporteHorarios>(`/api/tenants/${tenantId}/reports/horarios`, { query });
}

/** `GET .../reports/campanas` — rendimiento y CPA por anuncio o por campaña, con `avisos`. */
export function fetchReportCampanas(tenantId: string, query: QueryParams): Promise<ReporteCampanas> {
  return api.get<ReporteCampanas>(`/api/tenants/${tenantId}/reports/campanas`, { query });
}

/** `GET .../reports/productos` — ranking de productos vendidos (`limite` 1..100, `ordenar`). */
export function fetchReportProductos(
  tenantId: string,
  query: QueryParams,
): Promise<ReporteProductos> {
  return api.get<ReporteProductos>(`/api/tenants/${tenantId}/reports/productos`, { query });
}

/** `GET .../reports/motivos-no-cierre` — por qué no cerró: conversaciones que nunca tuvieron orden. */
export function fetchReportMotivosNoCierre(
  tenantId: string,
  query: QueryParams,
): Promise<ReporteMotivosNoCierre> {
  return api.get<ReporteMotivosNoCierre>(`/api/tenants/${tenantId}/reports/motivos-no-cierre`, {
    query,
  });
}
